import { useQuery } from '@tanstack/react-query';
import { StarIcon } from '@heroicons/react/24/solid';
import useApi from 'utils/use-api';
import dayjs from 'dayjs';

interface Review {
  id: number;
  requestId: number;
  reviewerName: string;
  reviewerEmail: string;
  assigneeId: number;
  assigneeName: string;
  rating: number;
  reviewText: string | null;
  createdAt: string;
  request: {
    id: number;
    category: string;
    details: string;
    requesterName: string;
    createdAt: string;
    completedAt: string;
  };
}

const categoryLabels: Record<string, string> = {
  hardware: 'Hardware',
  system_access: 'System Access',
  software: 'Software',
  email: 'Email Account',
  communication: 'Communication Tools',
  training: 'Training Access',
  other: 'Other',
};

const ReviewsPage = () => {
  const api = useApi();

  const { data: reviews, isLoading } = useQuery<Review[]>({
    queryKey: ['all-reviews'],
    queryFn: async () => {
      const response = await api.get('/access-requests/reviews');
      return response.data;
    },
  });

  const formatDate = (dateString: string) => {
    try {
      return dayjs(dateString).format('MMM DD, YYYY');
    } catch {
      return dateString;
    }
  };

  const calculateAverageRating = () => {
    if (!reviews || reviews.length === 0) return 0;
    const sum = reviews.reduce((acc, review) => acc + review.rating, 0);
    return (sum / reviews.length).toFixed(1);
  };

  const getRatingDistribution = () => {
    if (!reviews) return { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviews.forEach(review => {
      distribution[review.rating as keyof typeof distribution]++;
    });
    return distribution;
  };

  const getAverageRatingByAssignee = () => {
    if (!reviews) return [];
    const assigneeMap: Record<string, { name: string; ratings: number[]; count: number }> = {};

    reviews.forEach(review => {
      if (!assigneeMap[review.assigneeId]) {
        assigneeMap[review.assigneeId] = {
          name: review.assigneeName,
          ratings: [],
          count: 0,
        };
      }
      assigneeMap[review.assigneeId].ratings.push(review.rating);
      assigneeMap[review.assigneeId].count++;
    });

    return Object.entries(assigneeMap)
      .map(([assigneeId, data]) => ({
        assigneeId: parseInt(assigneeId),
        name: data.name,
        averageRating: (data.ratings.reduce((a, b) => a + b, 0) / data.count).toFixed(1),
        reviewCount: data.count,
      }))
      .sort((a, b) => parseFloat(b.averageRating) - parseFloat(a.averageRating));
  };

  const distribution = getRatingDistribution();
  const assigneeRatings = getAverageRatingByAssignee();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading reviews...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Service Reviews</h1>
          <p className="mt-2 text-sm text-gray-600">Performance metrics and feedback from service requesters</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Average Rating</p>
                <p className="mt-2 text-3xl font-semibold text-gray-900">{calculateAverageRating()}</p>
              </div>
              <StarIcon className="h-12 w-12 text-yellow-400" />
            </div>
            <p className="mt-4 text-sm text-gray-500">Based on {reviews?.length || 0} reviews</p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm font-medium text-gray-600 mb-4">Rating Distribution</p>
            <div className="space-y-2">
              {[5, 4, 3, 2, 1].map(star => (
                <div key={star} className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700 w-4">{star}</span>
                  <StarIcon className="h-4 w-4 text-yellow-400" />
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-yellow-400 h-2 rounded-full"
                      style={{ width: `${reviews && reviews.length > 0 ? (distribution[star as keyof typeof distribution] / reviews.length) * 100 : 0}%` }}
                    />
                  </div>
                  <span className="text-sm text-gray-600 w-8 text-right">{distribution[star as keyof typeof distribution]}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm font-medium text-gray-600 mb-4">Top Performers</p>
            <div className="space-y-3">
              {assigneeRatings.slice(0, 5).map(assignee => (
                <div key={assignee.assigneeId} className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{assignee.name}</p>
                    <p className="text-xs text-gray-500">{assignee.reviewCount} reviews</p>
                  </div>
                  <div className="flex items-center gap-1 ml-2">
                    <StarIcon className="h-4 w-4 text-yellow-400" />
                    <span className="text-sm font-semibold text-gray-900">{assignee.averageRating}</span>
                  </div>
                </div>
              ))}
              {assigneeRatings.length === 0 && <p className="text-sm text-gray-500">No reviews yet</p>}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">All Reviews</h2>
          </div>
          <div className="divide-y divide-gray-200">
            {reviews && reviews.length > 0 ? (
              reviews.map(review => (
                <div key={review.id} className="p-6 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="flex items-center gap-1">
                          {[1, 2, 3, 4, 5].map(star => (
                            <StarIcon
                              key={star}
                              className={`h-5 w-5 ${star <= review.rating ? 'text-yellow-400' : 'text-gray-300'}`}
                            />
                          ))}
                        </div>
                        <span className="text-sm font-semibold text-gray-900">{review.rating}/5</span>
                      </div>
                      <div className="mb-3">
                        <p className="text-sm text-gray-900">
                          <span className="font-medium">Service Provider:</span> {review.assigneeName}
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Reviewed by:</span> {review.reviewerName}
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Request Category:</span>{' '}
                          <span className="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10">
                            {categoryLabels[review.request.category] || review.request.category}
                          </span>
                        </p>
                      </div>
                      {review.reviewText && (
                        <div className="bg-gray-50 rounded-lg p-3 mb-3">
                          <p className="text-sm text-gray-700">{review.reviewText}</p>
                        </div>
                      )}
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span>Request #{review.requestId}</span>
                        <span>Reviewed on {formatDate(review.createdAt)}</span>
                        <span>Completed on {formatDate(review.request.completedAt)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-12 text-center">
                <StarIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-semibold text-gray-900">No reviews yet</h3>
                <p className="mt-1 text-sm text-gray-500">Reviews will appear here once requesters rate completed services.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewsPage;
